/**
 * Format a Date using a simple template.
 * Tokens: YYYY, MM, DD, HH, mm, ss
 * @example formatDate(new Date('2024-03-15'), 'YYYY-MM-DD') // "2024-03-15"
 */
export function formatDate(date: Date, template: string): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return template
    .replace("YYYY", String(date.getFullYear()))
    .replace("MM", pad(date.getMonth() + 1))
    .replace("DD", pad(date.getDate()))
    .replace("HH", pad(date.getHours()))
    .replace("mm", pad(date.getMinutes()))
    .replace("ss", pad(date.getSeconds()));
}

/**
 * Add a number of days/months/years to a date (returns a new Date).
 */
export function addToDate(
  date: Date,
  amount: number,
  unit: "days" | "months" | "years"
): Date {
  const d = new Date(date);
  if (unit === "days") d.setDate(d.getDate() + amount);
  else if (unit === "months") d.setMonth(d.getMonth() + amount);
  else if (unit === "years") d.setFullYear(d.getFullYear() + amount);
  return d;
}

/**
 * Get the difference between two dates in a given unit.
 * @example diffDates(new Date('2024-01-01'), new Date('2024-04-01'), 'days') // 91
 */
export function diffDates(
  a: Date,
  b: Date,
  unit: "milliseconds" | "seconds" | "minutes" | "hours" | "days"
): number {
  const ms = b.getTime() - a.getTime();
  const divisors: Record<typeof unit, number> = {
    milliseconds: 1,
    seconds: 1000,
    minutes: 60_000,
    hours: 3_600_000,
    days: 86_400_000,
  };
  return Math.round(ms / divisors[unit]);
}

/**
 * Check if a date falls between two other dates (inclusive).
 */
export function isBetween(date: Date, start: Date, end: Date): boolean {
  const t = date.getTime();
  return t >= start.getTime() && t <= end.getTime();
}

/**
 * Check if two dates are on the same calendar day.
 */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Return a relative time string (e.g. "3 days ago", "in 2 hours").
 */
export function timeAgo(date: Date, now = new Date()): string {
  const diff = now.getTime() - date.getTime();
  const abs = Math.abs(diff);
  const future = diff < 0;
  const fmt = (n: number, unit: string) =>
    `${future ? "in " : ""}${n} ${unit}${n !== 1 ? "s" : ""}${future ? "" : " ago"}`;

  if (abs < 60_000) return future ? "in a moment" : "just now";
  if (abs < 3_600_000) return fmt(Math.round(abs / 60_000), "minute");
  if (abs < 86_400_000) return fmt(Math.round(abs / 3_600_000), "hour");
  if (abs < 2_592_000_000) return fmt(Math.round(abs / 86_400_000), "day");
  if (abs < 31_536_000_000) return fmt(Math.round(abs / 2_592_000_000), "month");
  return fmt(Math.round(abs / 31_536_000_000), "year");
}

/**
 * Get the start of a day, month, or year for a given date.
 */
export function startOf(date: Date, unit: "day" | "month" | "year"): Date {
  const d = new Date(date);
  if (unit === "day") { d.setHours(0, 0, 0, 0); }
  else if (unit === "month") { d.setDate(1); d.setHours(0, 0, 0, 0); }
  else if (unit === "year") { d.setMonth(0, 1); d.setHours(0, 0, 0, 0); }
  return d;
}

/**
 * Check if a given value is a valid Date object.
 */
export function isValidDate(val: unknown): val is Date {
  return val instanceof Date && !isNaN(val.getTime());
}
